import "./menu.css";

import { useEffect, useMemo, useRef, useState } from "react";
import Nav from "../Nav";
import Footer from "../Footer";
import fallbackImage from "../bobatea/images/imagecoming soon.png";
import heroImage from "../../images/oreo_left.webp";

import { Splide, SplideSlide } from "@splidejs/react-splide";
import "@splidejs/react-splide/css";

import { MENU_API_URL, MENU_API_KEY } from "../../config";

const FALLBACK_IMAGE = fallbackImage;

/* ---------- helpers ---------- */

const getDescriptionItems = (description) => {
  if (Array.isArray(description)) {
    return description.filter(Boolean).map(String);
  }

  if (typeof description === "string" && description.trim()) {
    return [description.trim()];
  }

  return [];
};

const getActiveVariants = (item) =>
  Array.isArray(item?.variants)
    ? item.variants.filter((v) => v?.active !== false)
    : [];

const getActiveModifierGroups = (item) =>
  Array.isArray(item?.modifierGroups)
    ? item.modifierGroups.filter(Boolean)
    : [];

const formatCategoryName = (name) =>
  name
    ? String(name).replace(/[-_]/g, " ").replace(/\s+/g, " ").trim()
    : "Menu";

const normaliseParentKey = (key) =>
  String(key || "")
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase();

const orderParentKeys = (menu) => {
  const keys = Object.keys(menu);

  return keys.sort((a, b) => {
    if (a === "desserts") return -1;
    if (b === "desserts") return 1;
    return 0;
  });
};

/* ---------- normalise menu ---------- */

const normaliseItems = (items) =>
  items
    .filter((item) => item && item.active !== false)
    .map((item) => ({
      ...item,
      categoryKey: item.categoryKey || item.category || "other",
      categoryName:
        item.categoryName ||
        formatCategoryName(item.categoryKey || item.category) ||
        "Other",
    }));

const normaliseMenu = (payload) => {
  const data = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.menu)
        ? payload.menu
        : payload && typeof payload === "object"
          ? [payload]
          : [];

  const menu = new Map();

  const addParentItems = (parentKey, parentItems) => {
    if (!Array.isArray(parentItems) || !parentItems.length) return;

    const key = normaliseParentKey(parentKey);
    const existing = menu.get(key) || {
      key,
      label: formatCategoryName(parentKey),
      items: [],
    };

    parentItems.forEach((entry) => {
      if (Array.isArray(entry?.products)) {
        existing.items.push(
          ...entry.products.map((item) => ({
            ...item,
            categoryKey: item.categoryKey || entry.categoryKey,
            categoryName: item.categoryName || entry.categoryName,
          })),
        );
      } else if (entry && (entry.name || entry.code)) {
        existing.items.push(entry);
      }
    });

    menu.set(key, existing);
  };

  data.forEach((entry) => {
    if (!entry || typeof entry !== "object") return;

    if (entry.name || entry.code) {
      addParentItems("menu", [entry]);
      return;
    }

    const groupedProperties = Object.entries(entry).filter(([, value]) =>
      Array.isArray(value),
    );

    if (groupedProperties.length > 0) {
      groupedProperties.forEach(([parentKey, parentItems]) =>
        addParentItems(parentKey, parentItems),
      );
      return;
    }

    /* Keep compatibility with a flat API response. */
    addParentItems("menu", [entry]);
  });

  return Object.fromEntries(
    Array.from(menu.values())
      .map((parent) => [parent.key, normaliseItems(parent.items)])
      .filter(([, items]) => items.length > 0),
  );
};

const choiceLabel = (group) => {
  const { minSelections: min, maxSelections: max } = group;

  if (min && min === max) {
    return `Choose ${min}`;
  }

  if (max) {
    return `Choose ${min || 0}–${max}`;
  }

  return "Optional";
};

/* ---------- product card ---------- */

const MenuProduct = ({ item, tone, onClick }) => {
  const variants = getActiveVariants(item);
  const modifiers = getActiveModifierGroups(item);

  const description = getDescriptionItems(item.description)
    .slice(0, 2)
    .join(" ");

  return (
    <button
      type="button"
      className={`menuCard tone${tone}`}
      onClick={() => onClick(item)}
    >
      <span className="menuCardPic">
        <img src={item.img || FALLBACK_IMAGE} alt="" loading="lazy" />
      </span>

      <span className="menuCardBody">
        <strong>{item.name || "Menu Item"}</strong>

        {description && <span className="menuCardDesc">{description}</span>}

        {(variants.length > 0 || modifiers.length > 0) && (
          <span className="menuCardTags">
            {variants.length > 0 && (
              <b>{item.variantLabel || "Multiple options"}</b>
            )}

            {modifiers.length > 0 && <b>Customisable</b>}
          </span>
        )}
      </span>
    </button>
  );
};

/* ---------- product modal ---------- */

const ProductModal = ({ product, tone, onClose }) => {
  useEffect(() => {
    if (!product) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = previousOverflow;

      window.removeEventListener("keydown", onKey);
    };
  }, [product, onClose]);

  if (!product) return null;

  const descriptions = getDescriptionItems(product.description);

  const variants = getActiveVariants(product);

  const groups = getActiveModifierGroups(product);

  return (
    <div className="menuModalOverlay" onClick={onClose} role="presentation">
      <div
        className="menuModal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={product.name || "Tubbees product"}
      >
        <button
          type="button"
          className="menuModalClose"
          onClick={onClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 6l12 12" />
            <path d="M18 6L6 18" />
          </svg>
        </button>

        <div className={`menuModalVisual tone${tone}`}>
          <img
            src={product.img || FALLBACK_IMAGE}
            alt={product.name || "Tubbees"}
          />
        </div>

        <div className="menuModalContent">
          <span className="menuEyebrow">
            {formatCategoryName(product.categoryName)}
          </span>

          <h2>{product.name || product.categoryName}</h2>

          {descriptions.length > 0 && (
            <p className="menuModalDescription">{descriptions.join(" ")}</p>
          )}

          {variants.length > 0 && (
            <section className="menuModalSection">
              <h4>{product.variantLabel || "Options"}</h4>

              {variants.map((variant) => (
                <div
                  className="menuVariant"
                  key={variant.id || variant.code || variant.name}
                >
                  <b>{variant.name}</b>

                  {variant.description && <span>{variant.description}</span>}

                  {variant.includedScoops && (
                    <span>
                      Includes {variant.includedScoops} scoop
                      {variant.includedScoops === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
              ))}
            </section>
          )}

          {groups.map((group) => {
            const options = Array.isArray(group.options)
              ? group.options.filter((o) => o?.active !== false)
              : [];

            return (
              <section
                className="menuModalSection"
                key={group.id || group.name}
              >
                <h4>
                  {group.name}

                  <em>{choiceLabel(group)}</em>
                </h4>

                {options.length > 0 && (
                  <div className="menuOptionList">
                    {options.map((o) => (
                      <span key={o.id || o.name}>{o.name}</span>
                    ))}
                  </div>
                )}

                {group.requiresConfiguration && (
                  <p className="menuConfigNote">
                    Chosen in store when you order.
                  </p>
                )}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ---------- page ---------- */

const Menu = () => {
  const [menuItems, setMenuItems] = useState({});

  const [activeParent, setActiveParent] = useState("");

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const [search, setSearch] = useState("");

  const [activeCategory, setActiveCategory] = useState("");

  const [selected, setSelected] = useState(null);

  const [attempt, setAttempt] = useState(0);

  /* Splide instance */
  const categorySplideRef = useRef(null);

  const [railAtTop, setRailAtTop] = useState(false);

  /* ---------- load menu ---------- */

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        /* ---------- validate configuration ---------- */

        if (!MENU_API_URL) {
          throw new Error("REACT_APP_MENU_API_URL is not configured.");
        }

        if (!MENU_API_KEY) {
          throw new Error("REACT_APP_MENU_API_KEY is not configured.");
        }
        const baseUrl = new URL(MENU_API_URL);

        const firstUrl = new URL(baseUrl.toString());

        firstUrl.searchParams.set("active", "true");

        firstUrl.searchParams.set("page", "1");

        firstUrl.searchParams.set("limit", "100");

        const firstResponse = await fetch(firstUrl.toString(), {
          method: "GET",
          headers: {
            "x-api-key": MENU_API_KEY,
            Accept: "application/json",
          },
        });

        if (!firstResponse.ok) {
          if (firstResponse.status === 401) {
            throw new Error("Menu API rejected the API key (401).");
          }

          if (firstResponse.status === 404) {
            throw new Error(
              "Menu API endpoint was not found (404). Check REACT_APP_MENU_API_URL.",
            );
          }

          if (firstResponse.status === 400) {
            throw new Error("Menu API rejected the request (400).");
          }

          throw new Error(`Menu request failed (${firstResponse.status})`);
        }

        const firstPayload = await firstResponse.json();

        const allData =
          Array.isArray(firstPayload?.data) || Array.isArray(firstPayload)
            ? Array.isArray(firstPayload?.data)
              ? firstPayload.data
              : firstPayload
            : [firstPayload];

        const totalPages = Number(firstPayload?.pagination?.totalPages) || 1;

        /*
         * If there are more pages,
         * request them one by one.
         */
        for (let page = 2; page <= totalPages; page++) {
          const pageUrl = new URL(baseUrl.toString());

          pageUrl.searchParams.set("active", "true");

          pageUrl.searchParams.set("page", String(page));

          pageUrl.searchParams.set("limit", "100");

          const pageResponse = await fetch(pageUrl.toString(), {
            method: "GET",
            headers: {
              "x-api-key": MENU_API_KEY,
              Accept: "application/json",
            },
          });

          if (!pageResponse.ok) {
            throw new Error(
              `Menu request failed while loading page ${page} (${pageResponse.status})`,
            );
          }

          const pagePayload = await pageResponse.json();

          if (Array.isArray(pagePayload?.data)) {
            allData.push(...pagePayload.data);
          }
        }

        const normalised = normaliseMenu(allData);

        if (!mounted) return;

        setMenuItems(normalised);

        const firstParent = orderParentKeys(normalised)[0] || "";

        setActiveParent((current) =>
          current && normalised[current] ? current : firstParent,
        );
        setActiveCategory(normalised[firstParent]?.[0]?.categoryKey || "");
      } catch (err) {
        console.error("Failed to load Tubbees menu:", err);

        if (mounted) {
          setError(
            err?.message ||
              "We couldn't load the menu right now. Please try again.",
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [attempt]);

  const parentTabs = useMemo(
    () =>
      orderParentKeys(menuItems).map((key) => ({
        key,
        label: formatCategoryName(key.replace(/-/g, " ")).replace(
          /\b\w/g,
          (letter) => letter.toUpperCase(),
        ),
      })),
    [menuItems],
  );

  const items = useMemo(
    () => menuItems[activeParent] || [],
    [activeParent, menuItems],
  );

  const query = search.trim().toLowerCase();

  /* ---------- categories ---------- */

  const categories = useMemo(() => {
    const map = new Map();

    items.forEach((item) => {
      if (!map.has(item.categoryKey)) {
        map.set(item.categoryKey, {
          key: item.categoryKey,
          name: item.categoryName,
          items: [],
        });
      }

      map.get(item.categoryKey).items.push(item);
    });

    return Array.from(map.values());
  }, [items]);

  /* ---------- search ---------- */

  const sections = useMemo(() => {
    if (!query) return categories;

    return categories
      .map((cat) => ({
        ...cat,

        items: cat.items.filter((item) =>
          [
            item.name,
            item.categoryName,

            ...getDescriptionItems(item.description),

            ...getActiveVariants(item).flatMap((v) => [v.name, v.description]),

            ...getActiveModifierGroups(item).flatMap((g) => [
              g.name,

              ...(Array.isArray(g.options)
                ? g.options.map((o) => o?.name)
                : []),
            ]),
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(query),
        ),
      }))
      .filter((cat) => cat.items.length > 0);
  }, [categories, query]);

  const resultCount = sections.reduce((n, c) => n + c.items.length, 0);

  const switchParent = (parent) => {
    if (parent === activeParent) return;

    const nextItems = menuItems[parent] || [];

    setActiveParent(parent);
    setSearch("");
    setActiveCategory(nextItems[0]?.categoryKey || "");
  };

  /* ---------- scroll handling ---------- */

  useEffect(() => {
    if (loading || !sections.length) {
      return undefined;
    }

    const onScroll = () => {
      let current = sections[0].key;

      sections.forEach((cat) => {
        const el = document.getElementById(`menu-category-${cat.key}`);

        if (el && el.getBoundingClientRect().top < 200) {
          current = cat.key;
        }
      });

      setActiveCategory(current);

    };

    window.addEventListener("scroll", onScroll, { passive: true });

    onScroll();

    return () => window.removeEventListener("scroll", onScroll);
  }, [sections, loading]);

  /* ---------- menu rail position ---------- */

  useEffect(() => {
    const NAV_HEIGHT = 76;

    const checkRailPosition = () => {
      const rail = document.querySelector(".menuCategoryRail");

      if (!rail) {
        setRailAtTop(false);
        return;
      }

      const railTop = rail.getBoundingClientRect().top;

      setRailAtTop(railTop <= NAV_HEIGHT);
    };

    window.addEventListener("scroll", checkRailPosition, { passive: true });

    window.addEventListener("resize", checkRailPosition);

    checkRailPosition();

    return () => {
      window.removeEventListener("scroll", checkRailPosition);

      window.removeEventListener("resize", checkRailPosition);
    };
  }, []);

  /* ---------- keep active Splide tab visible ---------- */

  useEffect(() => {
    if (!activeCategory) return;

    const splide = categorySplideRef.current?.splide;

    if (!splide) return;

    const index = categories.findIndex((cat) => cat.key === activeCategory);

    if (index === -1) return;

    splide.go(index);
  }, [activeCategory, categories]);

  /* ---------- category navigation ---------- */

  const scrollToCategory = (key) => {
    setActiveCategory(key);

    const go = () => {
      const el = document.getElementById(`menu-category-${key}`);

      if (!el) return;

      window.scrollTo({
        top: el.getBoundingClientRect().top + window.scrollY - 180,
        behavior: "smooth",
      });
    };

    if (search) {
      setSearch("");

      requestAnimationFrame(() => requestAnimationFrame(go));
    } else {
      go();
    }
  };

  /* ---------- product ---------- */

  const openProduct = (item, tone) => {
    setSelected({
      item,
      tone,
    });
  };

  /* ---------- render ---------- */

  return (
    <div className={`tubbeesMenuPage ${railAtTop ? "menuRailActive" : ""}`}>
      <Nav />

      <main className="tubbeesMenuMain">
        {/* HERO */}

        <section className="menuHero">
          <div className="menuHeroInner">
            <div className="menuHeroCopy">
              <span className="menuEyebrow">TUBBEES / THE MENU</span>

              <h1>
                PICK YOUR
                <span>CRAVING.</span>
              </h1>

              <p>
                From pancakes, waffles and crepes to shakes, sundaes, ice cream
                and loaded treats — find your next Tubbees favourite.
              </p>

              <div className="menuHeroStats">
                <div>
                  <strong>{loading ? "—" : items.length}</strong>

                  <span>MENU ITEMS</span>
                </div>

                <i />

                <div>
                  <strong>{loading ? "—" : categories.length}</strong>

                  <span>CATEGORIES</span>
                </div>
              </div>
            </div>

            <div className="menuHeroBoard">
              <div className="menuHeroBoardLines">
                <span />
                <span />
                <span />
                <span />
              </div>

              <div className="menuHeroBoardCircle">
                <img src={heroImage} alt="" />
              </div>
            </div>
          </div>
        </section>

        {/* SEARCH */}

        <div className="menuParentTabs" role="tablist" aria-label="Menu type">
          {parentTabs.map((parent) => (
            <button
              type="button"
              role="tab"
              aria-selected={parent.key === activeParent}
              className={parent.key === activeParent ? "active" : ""}
              key={parent.key}
              onClick={() => switchParent(parent.key)}
            >
              {parent.label}
            </button>
          ))}
        </div>

        <div className="menuSearchWrap">
          <label className="menuSearch">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle
                cx="11"
                cy="11"
                r="6.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
              />

              <path
                d="M16 16l4 4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>

            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search the menu…"
              aria-label="Search menu"
            />

            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </label>
        </div>

        {/* SPLIDE CATEGORY RAIL */}

        <div
          className="menuCategoryRail"
          role="tablist"
          aria-label="Menu categories"
        >
          <Splide
            ref={categorySplideRef}
            options={{
              autoWidth: true,
              gap: "8px",
              arrows: false,
              pagination: false,
              drag: true,
              snap: true,
              rewind: false,
              speed: 500,
              easing: "cubic-bezier(0.22, 1, 0.36, 1)",
              trimSpace: true,
              padding: {
                left: "3vw",
                right: "3vw",
              },
              breakpoints: {
                700: {
                  gap: "6px",
                  padding: {
                    left: "16px",
                    right: "16px",
                  },
                },
              },
            }}
            aria-label="Menu categories"
          >
            {categories.map((cat) => (
              <SplideSlide key={cat.key}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={cat.key === activeCategory}
                  className={cat.key === activeCategory ? "active" : ""}
                  onClick={() => scrollToCategory(cat.key)}
                >
                  {cat.name}
                </button>
              </SplideSlide>
            ))}
          </Splide>
        </div>

        {/* CONTENT */}

        <div className="menuContent">
          {loading && (
            <div className="menuMessage">
              <div className="menuLoader" />

              <span>Loading the menu…</span>
            </div>
          )}

          {!loading && error && (
            <div className="menuMessage">
              <h3>Menu unavailable</h3>

              <p>{error}</p>

              <button
                type="button"
                className="menuRetry"
                onClick={() => setAttempt((n) => n + 1)}
              >
                Try again
              </button>
            </div>
          )}

          {!loading && !error && query && resultCount === 0 && (
            <div className="menuMessage">
              <h3>Nothing found</h3>

              <p>No results for “{search}”. Try a different word.</p>

              <button
                type="button"
                className="menuRetry"
                onClick={() => setSearch("")}
              >
                Clear search
              </button>
            </div>
          )}

          {!loading &&
            !error &&
            sections.map((cat, ci) => (
              <section
                className="menuGroup"
                id={`menu-category-${cat.key}`}
                key={cat.key}
              >
                <h2>
                  {cat.name}

                  <small>
                    {cat.items.length} item
                    {cat.items.length === 1 ? "" : "s"}
                  </small>
                </h2>

                <div className="menuGrid">
                  {cat.items.map((item, i) => {
                    const tone = (ci + i) % 4;

                    return (
                      <MenuProduct
                        key={item.id || `${cat.key}-${item.name}-${i}`}
                        item={item}
                        tone={tone}
                        onClick={(it) => openProduct(it, tone)}
                      />
                    );
                  })}
                </div>
              </section>
            ))}
        </div>
      </main>

      {/* PRODUCT MODAL */}

      <ProductModal
        product={selected?.item}
        tone={selected?.tone ?? 0}
        onClose={() => setSelected(null)}
      />

      {/* SCROLL TO TOP */}

      {/* <button
        type="button"
        className={`menuScrollTop ${showScrollTop ? "visible" : ""}`}
        onClick={scrollToTop}
        aria-label="Scroll to top"
        aria-hidden={!showScrollTop}
        tabIndex={showScrollTop ? 0 : -1}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 19V5" />
          <path d="M6 11l6-6 6 6" />
        </svg>
      </button> */}

      <Footer />
    </div>
  );
};

export default Menu;
